import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Upload, Sparkles, Download, Trash2, Copy } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DOC_TYPES = [
  { value: 'preventivo', label: 'Preventivo' },
  { value: 'fattura', label: 'Fattura' },
  { value: 'ddt', label: 'DDT' },
  { value: 'conteggio', label: 'Conteggio' },
  { value: 'generico', label: 'Generico' },
];

/**
 * Pagina "Modelli documenti" — `/admin/impostazioni/modelli-documenti`.
 *
 * Permette di:
 *  - caricare un .docx con placeholder Jinja `{{ var }}`
 *  - generare un nuovo template tramite prompt AI (GPT-5.2)
 *  - scaricare / duplicare / eliminare i template esistenti
 */
export default function TemplateDocumentiPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [aiDialog, setAiDialog] = useState(false);
  const [busy, setBusy] = useState(false);

  const [uploadForm, setUploadForm] = useState({ name: '', doc_type: 'preventivo', description: '', file: null });
  const [aiForm, setAiForm] = useState({ name: '', doc_type: 'preventivo', prompt: '' });

  const fetchTemplates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/document-templates/list`, { withCredentials: true });
      setTemplates(r.data || []);
    } catch {
      toast.error('Impossibile caricare i modelli');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) { toast.error('Seleziona un file .docx'); return; }
    const fd = new FormData();
    fd.append('file', uploadForm.file);
    fd.append('name', uploadForm.name);
    fd.append('doc_type', uploadForm.doc_type);
    fd.append('description', uploadForm.description || '');
    setBusy(true);
    try {
      await axios.post(`${API}/document-templates/upload`, fd, { withCredentials: true });
      toast.success('Modello caricato');
      setUploadDialog(false);
      setUploadForm({ name: '', doc_type: 'preventivo', description: '', file: null });
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload fallito');
    } finally {
      setBusy(false);
    }
  };

  const handleAiGenerate = async (e) => {
    e.preventDefault();
    if (!aiForm.prompt || aiForm.prompt.length < 10) {
      toast.error('Descrivi il documento (almeno 10 caratteri)');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/document-templates/generate-ai`, aiForm, { withCredentials: true });
      toast.success('Modello generato dall\'AI');
      setAiDialog(false);
      setAiForm({ name: '', doc_type: 'preventivo', prompt: '' });
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Generazione fallita');
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = (id, name) => {
    window.open(`${API}/document-templates/${id}/download`, '_blank');
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API}/document-templates/${id}/duplicate`, {}, { withCredentials: true });
      toast.success('Duplicato');
      fetchTemplates();
    } catch { toast.error('Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo modello?')) return;
    try {
      await axios.delete(`${API}/document-templates/${id}`, { withCredentials: true });
      toast.success('Eliminato');
      fetchTemplates();
    } catch { toast.error('Errore'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><FileText className="h-6 w-6" /> Modelli documenti</h1>
          <p className="text-sm text-slate-500 mt-1">Carica un modello Microsoft Word (.dotx) con placeholder Jinja (es. {`{{ cliente_nome }}`}) oppure generane uno con l'AI a partire da un prompt.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setUploadDialog(true)} data-testid="open-upload-template"><Upload className="h-4 w-4 mr-2" />Carica .dotx</Button>
          <Button className="bg-slate-900 text-white" onClick={() => setAiDialog(true)} data-testid="open-ai-template"><Sparkles className="h-4 w-4 mr-2" />Genera con AI</Button>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Nome</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Origine</TableHead>
                <TableHead>Placeholder</TableHead>
                <TableHead>Descrizione</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">Nessun modello — caricane uno o generalo con l'AI</TableCell></TableRow>
              ) : templates.map(t => (
                <TableRow key={t.id} data-testid={`template-${t.id}`}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell><Badge variant="outline">{t.doc_type}</Badge></TableCell>
                  <TableCell>
                    <Badge className={t.source === 'ai' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                      {t.source === 'ai' ? 'AI' : 'Upload'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-md">{(t.placeholders || []).slice(0, 6).join(', ')}{(t.placeholders?.length || 0) > 6 ? '…' : ''}</TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-xs truncate">{t.description || '-'}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => downloadTemplate(t.id, t.name)} title="Scarica .docx" data-testid={`dl-template-${t.id}`}><Download className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDuplicate(t.id)} title="Duplica"><Copy className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="text-red-600" onClick={() => handleDelete(t.id)} title="Elimina"><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Upload dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Carica modello .dotx</DialogTitle>
            <DialogDescription>Carica un modello Microsoft Word (.dotx) con placeholder Jinja per l'auto-compilazione.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div><Label>Nome modello</Label><Input value={uploadForm.name} onChange={(e) => setUploadForm({...uploadForm, name: e.target.value})} required data-testid="upload-tpl-name" /></div>
            <div><Label>Tipo documento</Label>
              <Select value={uploadForm.doc_type} onValueChange={(v) => setUploadForm({...uploadForm, doc_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Descrizione (opzionale)</Label><Input value={uploadForm.description} onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})} /></div>
            <div>
              <Label>File modello Word (.dotx)</Label>
              <Input type="file" accept=".dotx" onChange={(e) => setUploadForm({...uploadForm, file: e.target.files?.[0] || null})} required data-testid="upload-tpl-file" />
              <p className="text-xs text-slate-500 mt-1">Solo modelli Word <code>.dotx</code>. Usa placeholder Jinja: <code>{`{{ cliente_nome }}`}</code>, <code>{`{{ data }}`}</code>, <code>{`{{ totale }}`}</code>, ecc.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setUploadDialog(false)}>Annulla</Button>
              <Button type="submit" disabled={busy} className="bg-slate-900 text-white" data-testid="upload-tpl-submit">{busy ? 'Caricamento…' : 'Carica'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI generate dialog */}
      <Dialog open={aiDialog} onOpenChange={setAiDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Genera modello con AI</DialogTitle></DialogHeader>
          <form onSubmit={handleAiGenerate} className="space-y-4">
            <div><Label>Nome modello</Label><Input value={aiForm.name} onChange={(e) => setAiForm({...aiForm, name: e.target.value})} required placeholder="es. Preventivo elegante con tabella" data-testid="ai-tpl-name" /></div>
            <div><Label>Tipo documento</Label>
              <Select value={aiForm.doc_type} onValueChange={(v) => setAiForm({...aiForm, doc_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descrizione (prompt)</Label>
              <Textarea value={aiForm.prompt} onChange={(e) => setAiForm({...aiForm, prompt: e.target.value})} rows={5} required placeholder="es. Crea un preventivo elegante con tabella lavorazioni, sezione note, condizioni di pagamento a 30gg e spazio firma in basso" data-testid="ai-tpl-prompt" />
              <p className="text-xs text-slate-500 mt-1">Powered by GPT-5.2. Più sei specifico, migliore sarà il risultato.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setAiDialog(false)}>Annulla</Button>
              <Button type="submit" disabled={busy} className="bg-slate-900 text-white" data-testid="ai-tpl-submit"><Sparkles className="h-4 w-4 mr-2" />{busy ? 'Generazione…' : 'Genera'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
